// The workspace's security posture (#639, D14): the score and what it is made
// of, the compliance frameworks a scan is read against, Cyber Essentials, the
// Risks and their mutes, and the findings behind all of it.
//
// Read off `routes/posture.py`. Two readings are the point and every screen
// keeps them: a score is out of `score_max`, never out of 100 when a component
// could not be read (detection coverage is not measured in Phase 3);
// and a framework's rate is over the requirements a machine decided, beside
// `framework_total`, `not_assessed` and `manual`, never alone.

import type { Api } from "./http";

export const POSTURE_PATH = "/api/v1/posture";
export const POSTURE_OVERVIEW_PATH = `${POSTURE_PATH}/overview`;
export const CYBER_ESSENTIALS_PATH = `${POSTURE_PATH}/cyber-essentials`;
export const RISKS_PATH = `${POSTURE_PATH}/risks`;
export const FINDINGS_PATH = `${POSTURE_PATH}/findings`;
export const SCANS_PATH = `${POSTURE_PATH}/scans`;
export const SCAN_AVAILABILITY_PATH = `${SCANS_PATH}/availability`;

export const compliancePath = (scanId: string): string =>
  `${POSTURE_PATH}/scans/${scanId}/compliance`;
export const frameworkPath = (scanId: string, frameworkId: string): string =>
  `${compliancePath(scanId)}/${frameworkId}`;
export const riskPath = (riskId: string): string => `${RISKS_PATH}/${riskId}`;

/** One of the live score's three components, scored or not (formula version 3, #1283). */
export interface ScoreComponent {
  key: "risks" | "findings" | "coverage";
  label: string;
  scored: boolean;
  weight_pct: number;
  /** Null when the component could not be read: it lowers the ceiling, it deducts nothing. */
  pressure: number | null;
  points_lost: number | null;
  points_unearnable: number;
  detail: Record<string, unknown>;
}

export interface AccountScore {
  account_id: string;
  account_alias: string;
  /** What the workspace calls the account, or null: the id is shown (#823). */
  name: string | null;
  scanned: boolean;
  score: number | null;
  score_max?: number;
  components: ScoreComponent[];
  scan_id?: string;
  completed_at?: string;
}

/**
 * A stored snapshot, read back as it was written: its formula version and component set
 * are its own, so a version-2 point may name "paths" (#1283).
 */
export interface TrendPoint {
  score: number;
  score_max: number;
  computed_at: string;
  scan_id: string;
  formula_version: number;
  scored_components: string[];
}

export interface DriftSummary {
  scan_id: string;
  baseline_scan_id: string;
  account_id: string;
  has_baseline: boolean;
  baseline_has_path_snapshot: boolean;
  path_snapshot_complete: boolean;
  computed_at: string;
  new_findings_count: number;
  resolved_findings_count: number;
  opened_paths_count: number;
  closed_paths_count: number;
  newly_exposed_count: number;
}

export interface PostureScore {
  score: number | null;
  score_raw: number | null;
  score_max: number;
  has_data: boolean;
  account_count: number;
  components: ScoreComponent[];
  per_account: AccountScore[];
  scored_components: string[];
  formula_version: number;
  trend: TrendPoint[];
  drift: DriftSummary | null;
}

/** A framework's result, as a scan's compliance answers it (no requirements). */
export interface FrameworkResult {
  framework_id: string;
  passed: number;
  failed: number;
  manual: number;
  total: number;
  framework_total: number;
  not_assessed: number;
  pass_rate: number;
  /** The Prowler that ran the scan, as it recorded it; null if it recorded none (#1279). */
  scanned_with?: string | null;
  /**
   * The Prowler whose framework definitions the scan is read against, when that is not the
   * one that ran it: the scan is reassessed (#1273, the owner's decision 2). Else null.
   */
  reassessed_under?: string | null;
}

export interface Requirement {
  requirement_id: string;
  title: string;
  status: string;
}

export interface FrameworkDetail extends FrameworkResult {
  requirements: Requirement[];
}

export interface PostureOverview {
  total_findings: number;
  fail_count: number;
  pass_count: number;
  manual_count: number;
  severity_counts: Record<string, number>;
  top_failing_services: { service: string; count: number }[];
  top_failing_checks: { check_id: string; check_title: string; count: number }[];
  compliance_summary: Record<string, Omit<FrameworkResult, "framework_id">>;
  accounts_scanned: number;
  active_risks: number;
  muted_risks: number;
  last_scan_at: string | null;
  last_scan_id: string | null;
}

export interface CyberEssentialsControl {
  id: string;
  name: string;
  coverage: "partial" | "weak" | "none";
  status: "PASS" | "FAIL" | "NOT_OBSERVABLE" | "NOT_ASSESSED";
  what_we_evidence: string;
  out_of_scope: string;
  checks_evidenced: string[];
  checks_failing: string[];
  detector_checks_seen: string[];
}

export interface CyberEssentialsView {
  scheme: string;
  controls: CyberEssentialsControl[];
  scan_ran: boolean;
}

export interface Risk {
  risk_id: string;
  resource_arn: string;
  resource_name: string;
  account_id: string;
  signals: string[];
  severity: string;
  score: number;
  state: string;
  muted: boolean;
  muted_reason: string;
  mute_lapsed: boolean;
  unaccepted_signals: string[];
}

export interface RisksPage {
  items: Risk[];
  next_cursor: string | null;
  total: number;
  active: number;
  muted: number;
  may_mute: boolean;
}

export interface Finding {
  id: string;
  scan_id: string;
  account_id: string;
  /** What the workspace calls the account, or null: the id is shown (#823). */
  account_name: string | null;
  finding_id: string;
  check_id: string;
  check_title: string;
  status: "PASS" | "FAIL" | "MANUAL";
  severity: string;
  service_name: string;
  region: string;
  resource_uid: string;
  resource_name: string;
  status_extended: string;
  risk: string;
  remediation: { recommendation?: string; url?: string; cli?: string };
  muted: boolean;
}

export interface FindingsPage {
  items: Finding[];
  next_cursor: string | null;
  /** How many findings the filters match, across every page (#870). */
  total: number;
}

export interface FindingsFilter {
  status?: "FAIL" | "PASS" | "MANUAL";
  severity?: string;
  /** One scan's findings; absent, every account's current scan. */
  scan_id?: string;
  /** The AWS service's short name, as the scanner writes it (`s3`, `iam`). */
  service?: string;
  cursor?: string | null;
}

/** An account a scan may be started for. */
export interface ScanAccount {
  cloud_account_id: string;
  account_id: string;
  /** What the workspace calls the account, or null: the id is shown (#823). */
  name: string | null;
}

/** A scan's state as the list shows it (#821). */
export type ScanStatus = "running" | "ingesting" | "done" | "failed";

/**
 * Why a scan failed: the closed set of reason codes (`posture/scans.py::FailureReason`,
 * `0088_scan_failure_reason.sql`, #1276). The server decides; the console words.
 */
export type ScanFailureReason =
  | "launch_failed"
  | "no_output"
  | "scanner_error"
  | "malformed_output"
  | "ingest_error"
  | "account_disconnected"
  | "timed_out";

export interface ScanRecord {
  scan_id: string;
  cloud_account_id: string;
  account_id: string;
  account_name: string | null;
  status: ScanStatus;
  origin: "manual" | "scheduled";
  started_at: string;
  completed_at: string | null;
  finding_count: number | null;
  /** Whether its findings are still kept: the current scan and the one it superseded (#870). */
  findings_held: boolean;
  /** Why a failed scan failed, as one code of the closed set; null unless `failed` (#1276). */
  failure_reason: ScanFailureReason | null;
}

/** `GET /api/v1/posture/scans`: the accounts, their scans newest first, and whether this
 * session may start one (`posture:scan`). */
export interface ScansView {
  accounts: ScanAccount[];
  items: ScanRecord[];
  may_scan: boolean;
}

/**
 * Why a scan may not start on an account: the admission policy's closed set of reason codes
 * (`control_plane/scan_admission.py::Reason`, #1274). The server decides; the console words.
 */
export type ScanRefusal =
  | "no_permission"
  | "grant_lacks_scan"
  | "not_shared_for_scan"
  | "batch_running"
  | "batch_marker_unreadable"
  | "scanner_not_configured"
  | "account_not_found"
  | "organization_member"
  | "role_not_scannable"
  | "scan_active"
  | "account_not_active"
  | "too_soon";

/** One account as the admission policy answers it, read-only (#1274). */
export interface ScanAvailability {
  cloud_account_id: string;
  account_id: string;
  name: string | null;
  may_scan: boolean;
  /** Null when a scan may start. */
  reason: ScanRefusal | null;
  /** When the account last finished a scan; null if it never has. */
  last_scan_at: string | null;
  /** While the gap between manual scans holds, when the next is allowed; else null. */
  next_allowed_at: string | null;
}

/** `GET /api/v1/posture/scans/availability`: every account, and the roles that may scan. */
export interface AvailabilityView {
  accounts: ScanAvailability[];
  /** The workspace's roles holding `posture:scan`, by rank: who can start a scan. */
  scan_roles: string[];
}

/** Everything the posture screen reads at once. */
export interface PostureView {
  score: PostureScore;
  overview: PostureOverview;
  /** The most recent scan's frameworks; empty before any scan. */
  compliance: FrameworkResult[];
  cyberEssentials: CyberEssentialsView;
  risks: RisksPage;
  /** Null when the scans could not be read: the rest of the page stands without them. */
  scans: ScansView | null;
  /** Null when whether each account can be scanned could not be read (#1274). */
  availability: AvailabilityView | null;
}

/** The frameworks a scan may be read against, in the server's order (`compliance.py`). */
export const SELECTABLE_FRAMEWORKS = [
  "cis_7.0_aws",
  "cis_6.0_aws",
  "cis_5.0_aws",
  "cis_2.0_aws",
  "pci_4.0_aws",
  "pci_dss_v3.2.1_aws",
  "hipaa_aws",
  "gdpr_aws",
  "soc2_aws",
  "iso27001_2022_aws",
  "iso27001_2013_aws",
] as const;

/** The framework the Frameworks tab arrives on: the server's default (#1279). */
export const DEFAULT_FRAMEWORK = "cis_7.0_aws";

/** What the old scan picker called each (saas `ScanTriggerModal.tsx`). */
export const FRAMEWORK_LABELS: Record<string, string> = {
  "cis_7.0_aws": "CIS AWS 7.0",
  "cis_6.0_aws": "CIS AWS 6.0",
  "cis_5.0_aws": "CIS AWS 5.0",
  "cis_2.0_aws": "CIS AWS 2.0",
  "pci_4.0_aws": "PCI DSS v4.0",
  "pci_dss_v3.2.1_aws": "PCI DSS v3.2.1 (retired)",
  hipaa_aws: "HIPAA",
  gdpr_aws: "GDPR",
  soc2_aws: "SOC 2",
  iso27001_2022_aws: "ISO 27001:2022",
  iso27001_2013_aws: "ISO 27001:2013",
};

export const frameworkLabel = (id: string): string => FRAMEWORK_LABELS[id] ?? id;

/** Every selectable framework, read against one scan. */
export const readCompliance = (api: Api, scanId: string): Promise<FrameworkResult[]> =>
  api.get<FrameworkResult[]>(
    `${compliancePath(scanId)}?frameworks=${SELECTABLE_FRAMEWORKS.join(",")}`,
  );

export const readFramework = (
  api: Api,
  scanId: string,
  frameworkId: string,
): Promise<FrameworkDetail> => api.get<FrameworkDetail>(frameworkPath(scanId, frameworkId));

export async function readPosture(api: Api): Promise<PostureView> {
  const [score, overview, cyberEssentials, risks, scans, availability] = await Promise.all([
    api.get<PostureScore>(POSTURE_PATH),
    api.get<PostureOverview>(POSTURE_OVERVIEW_PATH),
    api.get<CyberEssentialsView>(CYBER_ESSENTIALS_PATH),
    api.get<RisksPage>(RISKS_PATH),
    readScans(api).catch(() => null),
    readAvailability(api).catch(() => null),
  ]);
  const compliance =
    overview.last_scan_id === null ? [] : await readCompliance(api, overview.last_scan_id);
  return { score, overview, compliance, cyberEssentials, risks, scans, availability };
}

export function findingsPath(filter: FindingsFilter = {}): string {
  const query = new URLSearchParams();
  if (filter.status) query.set("status", filter.status);
  if (filter.severity) query.set("severity", filter.severity);
  if (filter.scan_id) query.set("scan_id", filter.scan_id);
  if (filter.service) query.set("service", filter.service);
  if (filter.cursor) query.set("cursor", filter.cursor);
  const text = query.toString();
  return text === "" ? FINDINGS_PATH : `${FINDINGS_PATH}?${text}`;
}

export const readFindings = (api: Api, filter: FindingsFilter = {}): Promise<FindingsPage> =>
  api.get<FindingsPage>(findingsPath(filter));

export const muteRisk = (
  api: Api,
  riskId: string,
  muted: boolean,
  reason = "",
): Promise<unknown> => api.patch(riskPath(riskId), { muted, muted_reason: reason });

export const readScans = (api: Api): Promise<ScansView> => api.get<ScansView>(SCANS_PATH);

/** Whether each account can be scanned, and why not (#1274). */
export const readAvailability = (api: Api): Promise<AvailabilityView> =>
  api.get<AvailabilityView>(SCAN_AVAILABILITY_PATH);

/** Start a scan of one account; rejects with the route's refusal, in its words. */
export const startScan = (api: Api, cloudAccountId: string): Promise<ScanRecord> =>
  api.post<ScanRecord>(SCANS_PATH, { cloud_account_id: cloudAccountId });
